import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Paper,
    Typography,
    Button,
    Grid,
    Divider,
    Avatar,
    Chip,
    CircularProgress,
    Alert,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

import { useNavigate, useParams } from "react-router-dom";

import { getOrderById } from "../../services/orderService";
import { getOrderStatusColor, getOrderStatusLabelPt, normalizeOrderStatus, ORDER_STATUS, buildOrderTimeline } from "../../utils/orderStatus";
import { ref, push, set, update as dbUpdate } from "firebase/database";
import { rtdb } from "../../services/firebase";

function formatBRL(value) {
    return Number(value || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
    });
}

function formatDate(timestamp) {
    if (!timestamp) return "-";
    const d = new Date(timestamp);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    return `${dd}/${mm}/${yyyy} (${hh}:${min})`;
}

function getPaymentLabel(payment) {
    if (!payment?.method) return "N/A";
    return payment.method === "card" ? "Cartão" : "Pix";
}

export default function OrderDetails() {
    const navigate = useNavigate();
    const { id } = useParams();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [order, setOrder] = useState(null);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        status: ORDER_STATUS.PENDING_PAYMENT,
        trackingCode: "",
        trackingUrl: "",
    });

    useEffect(() => {
        async function load() {
            setLoading(true);
            setError("");

            try {
                const data = await getOrderById(id);
                if (!data) {
                    setError("Pedido não encontrado.");
                    setOrder(null);
                    return;
                }
                setOrder(data);
                setForm({
                    status: normalizeOrderStatus(data.status),
                    trackingCode: data.shipping?.trackingCode || "",
                    trackingUrl: data.shipping?.trackingUrl || "",
                });
            } catch (err) {
                console.error("Erro ao carregar pedido:", err);
                setError("Erro ao carregar pedido.");
                setOrder(null);
            } finally {
                setLoading(false);
            }
        }

        if (id) load();
    }, [id]);

    const addressLine = useMemo(() => {
        const a = order?.address;
        if (!a) return "-";
        const parts = [
            a.street ? `${a.street}, ${a.number || "S/N"}` : null,
            a.district || null,
            a.city ? `${a.city}${a.state ? ` - ${a.state}` : ""}` : null,
            a.cep ? `CEP: ${a.cep}` : null,
        ].filter(Boolean);
        return parts.join(" • ");
    }, [order]);

    const timeline = useMemo(() => {
        if (!order) return [];
        return buildOrderTimeline({
            status: order.status,
            createdAt: order.createdAt,
            events: order.events,
        });
    }, [order]);

    async function salvarAtualizacoes() {
        if (!order?.id) return;
        setSaving(true);

        const nextStatus = normalizeOrderStatus(form.status);
        const prevStatus = normalizeOrderStatus(order.status);

        const nextTrackingCode = (form.trackingCode || "").trim();
        const nextTrackingUrl = (form.trackingUrl || "").trim();

        try {
            await dbUpdate(ref(rtdb, `orders/${order.id}`), {
                status: nextStatus,
                updatedAt: Date.now(),
                shipping: {
                    ...(order.shipping || {}),
                    trackingCode: nextTrackingCode || null,
                    trackingUrl: nextTrackingUrl || null,
                },
            });

            // eventos
            if (nextStatus !== prevStatus) {
                const evRef = push(ref(rtdb, `orders/${order.id}/events`));
                await set(evRef, { type: "status", from: prevStatus, to: nextStatus, at: Date.now() });
            }

            const prevCode = order.shipping?.trackingCode || "";
            const prevUrl = order.shipping?.trackingUrl || "";
            if (prevCode !== nextTrackingCode || prevUrl !== nextTrackingUrl) {
                const evRef = push(ref(rtdb, `orders/${order.id}/events`));
                await set(evRef, { type: "tracking", at: Date.now() });
            }

            // atualiza estado local
            setOrder((prev) => ({
                ...prev,
                status: nextStatus,
                updatedAt: Date.now(),
                shipping: {
                    ...(prev?.shipping || {}),
                    trackingCode: nextTrackingCode,
                    trackingUrl: nextTrackingUrl,
                },
            }));
        } catch (e) {
            console.error("Erro ao salvar atualizações do pedido:", e);
            setError("Erro ao salvar alterações do pedido.");
        } finally {
            setSaving(false);
        }
    }

    function handleExportPdf() {
        if (!order) return;

        const html = `
<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Pedido ${order.id}</title>
    <style>
      body { font-family: Arial, sans-serif; padding: 24px; color: #0f172a; }
      h1 { font-size: 18px; margin: 0 0 12px; }
      .muted { color: #64748b; font-size: 12px; }
      .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-top: 16px; }
      .card { border: 1px solid #e2e8f0; border-radius: 10px; padding: 14px; }
      .row { display: flex; justify-content: space-between; gap: 12px; margin: 6px 0; font-size: 13px; }
      .row b { font-weight: 700; }
      table { width: 100%; border-collapse: collapse; margin-top: 16px; }
      th, td { border-bottom: 1px solid #e2e8f0; padding: 10px 6px; font-size: 13px; text-align: left; }
      th { color: #475569; font-weight: 700; }
      .right { text-align: right; }
      .total { font-size: 14px; font-weight: 800; }
      @media print { button { display: none; } }
    </style>
  </head>
  <body>
    <h1>Pedido #${order.id}</h1>
    <div class="muted">Data: ${order.createdAt ? new Date(order.createdAt).toLocaleString("pt-BR") : "-"}</div>

    <div class="grid">
      <div class="card">
        <div class="row"><span><b>Cliente</b></span><span></span></div>
        <div class="row"><span>Nome</span><span>${order.client?.name || "-"}</span></div>
        <div class="row"><span>CPF</span><span>${order.client?.cpf || "-"}</span></div>
        <div class="row"><span>Email</span><span>${order.client?.email || "-"}</span></div>
        <div class="row"><span>Telefone</span><span>${order.client?.phone || "-"}</span></div>
        <div class="row"><span>Endereço</span><span>${addressLine}</span></div>
        <div class="row"><span>Rastreio</span><span>${order.shipping?.trackingCode || "-"}</span></div>
      </div>

      <div class="card">
        <div class="row"><span><b>Pedido</b></span><span></span></div>
        <div class="row"><span>Status</span><span>${getOrderStatusLabelPt(order.status)}</span></div>
        <div class="row"><span>Pagamento</span><span>${getPaymentLabel(order.payment)}</span></div>
        <div class="row"><span>Frete</span><span>${formatBRL(order.shippingPrice || order.shipping?.price || 0)}</span></div>
        <div class="row"><span>Subtotal</span><span>${formatBRL(order.subtotal || 0)}</span></div>
        <div class="row total"><span>Total</span><span>${formatBRL(order.total || 0)}</span></div>
      </div>
    </div>

    <table>
      <thead>
        <tr>
          <th>Item</th>
          <th>Variação</th>
          <th class="right">Qtd</th>
          <th class="right">Preço</th>
          <th class="right">Total</th>
        </tr>
      </thead>
      <tbody>
        ${(order.items || []).map((it) => `
          <tr>
            <td>${it.productName || "-"}</td>
            <td>${it.flavor || "-"}</td>
            <td class="right">${Number(it.quantity || 0)}</td>
            <td class="right">${formatBRL(it.price || 0)}</td>
            <td class="right">${formatBRL((it.price || 0) * (it.quantity || 0))}</td>
          </tr>
        `).join("")}
      </tbody>
    </table>

    <script>
      window.onload = () => {
        window.focus();
        window.print();
      };
    </script>
  </body>
</html>`;

        const w = window.open("", "_blank");
        if (!w) return;
        w.document.open();
        w.document.write(html);
        w.document.close();
    }

    return (
        <Box sx={{ p: 4, ml: 32 }}>
            {loading && (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 240 }}>
                    <CircularProgress />
                </Box>
            )}

            {!loading && error && (
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                    {error}
                </Alert>
            )}

            {!loading && !error && order && (
                <>
                    {/* TOPO */}
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 4,
                        }}
                    >
                        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
                            Voltar
                        </Button>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Typography sx={{ fontWeight: 600 }}>{order.id}</Typography>

                            <Button
                                variant="contained"
                                startIcon={<PictureAsPdfIcon />}
                                onClick={handleExportPdf}
                            >
                                Exportar PDF
                            </Button>
                        </Box>
                    </Box>

                    {/* CARDS */}
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 3,
                            width: "100%",
                        }}
                    >
                        {/* CLIENTE */}
                        <Paper
                            sx={{
                                p: 3,
                                borderRadius: 3,
                                display: "flex",
                                flexDirection: "column",
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                                Cliente
                            </Typography>

                            <Typography sx={{ fontWeight: 600 }}>
                                {order.client?.name || "-"}
                            </Typography>
                            <Typography>CPF: {order.client?.cpf || "-"}</Typography>
                            <Typography>Email: {order.client?.email || "-"}</Typography>
                            <Typography>Telefone: {order.client?.phone || "-"}</Typography>

                            <Divider sx={{ my: 2 }} />

                            <Typography>{addressLine}</Typography>

                            <Typography sx={{ mt: 2 }}>
                                Código de rastreio:{" "}
                                <strong>{order.shipping?.trackingCode || "-"}</strong>
                            </Typography>
                        </Paper>

                        {/* PEDIDO */}
                        <Paper
                            sx={{
                                p: 3,
                                borderRadius: 3,
                                display: "flex",
                                flexDirection: "column",
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                                Pedido
                            </Typography>

                            <Typography>
                                Valor: <strong>{formatBRL(order.total || 0)}</strong>
                            </Typography>
                            <Typography>Classificação: -</Typography>
                            <Typography>Cupom: {order.coupon || "-"}</Typography>
                            <Typography>
                                Frete: {formatBRL(order.shippingPrice || order.shipping?.price || 0)}
                            </Typography>
                            <Typography>Transportadora: {order.shipping?.carrier || "-"}</Typography>
                            <Typography>Pagamento: {getPaymentLabel(order.payment)}</Typography>
                            <Typography>Data: {formatDate(order.createdAt)}</Typography>

                            <Box sx={{ mt: 2 }}>
                                <Chip
                                    label={getOrderStatusLabelPt(order.status)}
                                    color={getOrderStatusColor(order.status)}
                                />
                            </Box>
                        </Paper>
                    </Box>

                    {/* CONTROLES ADMIN */}
                    <Paper sx={{ p: 3, borderRadius: 3, mt: 4 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                            Atualizar status e rastreio
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <Typography sx={{ fontSize: 12, fontWeight: 700, opacity: 0.7, mb: 0.5 }}>
                                    Status
                                </Typography>
                                <select
                                    value={form.status}
                                    onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
                                    style={{
                                        width: "100%",
                                        height: 40,
                                        borderRadius: 8,
                                        border: "1px solid #e2e8f0",
                                        padding: "0 10px",
                                    }}
                                    disabled={saving}
                                >
                                    <option value={ORDER_STATUS.PENDING_PAYMENT}>Aguardando pagamento</option>
                                    <option value={ORDER_STATUS.PAID}>Pago</option>
                                    <option value={ORDER_STATUS.PICKING}>Em separação</option>
                                    <option value={ORDER_STATUS.SHIPPED}>Enviado</option>
                                    <option value={ORDER_STATUS.DELIVERED}>Entregue</option>
                                    <option value={ORDER_STATUS.CANCELLED}>Cancelado</option>
                                </select>
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <Typography sx={{ fontSize: 12, fontWeight: 700, opacity: 0.7, mb: 0.5 }}>
                                    Código de rastreio
                                </Typography>
                                <input
                                    value={form.trackingCode}
                                    onChange={(e) => setForm((p) => ({ ...p, trackingCode: e.target.value }))}
                                    placeholder="Ex: BR123456789BR"
                                    style={{
                                        width: "100%",
                                        height: 40,
                                        borderRadius: 8,
                                        border: "1px solid #e2e8f0",
                                        padding: "0 10px",
                                    }}
                                    disabled={saving}
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <Typography sx={{ fontSize: 12, fontWeight: 700, opacity: 0.7, mb: 0.5 }}>
                                    URL de rastreio
                                </Typography>
                                <input
                                    value={form.trackingUrl}
                                    onChange={(e) => setForm((p) => ({ ...p, trackingUrl: e.target.value }))}
                                    placeholder="https://..."
                                    style={{
                                        width: "100%",
                                        height: 40,
                                        borderRadius: 8,
                                        border: "1px solid #e2e8f0",
                                        padding: "0 10px",
                                    }}
                                    disabled={saving}
                                />
                            </Grid>
                        </Grid>

                        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                            <Button variant="contained" onClick={salvarAtualizacoes} disabled={saving}>
                                {saving ? "Salvando..." : "Salvar alterações"}
                            </Button>
                        </Box>
                    </Paper>

                    {/* TIMELINE */}
                    <Paper sx={{ p: 3, borderRadius: 3, mt: 4 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                            Timeline do pedido
                        </Typography>
                        {timeline.length === 0 ? (
                            <Typography sx={{ opacity: 0.7 }}>Sem eventos registrados.</Typography>
                        ) : (
                            <Box>
                                {timeline.map((t, idx) => (
                                    <Box key={`${t.label}-${idx}`} sx={{ py: 1, borderBottom: idx === timeline.length - 1 ? "none" : "1px solid #e2e8f0" }}>
                                        <Typography sx={{ fontWeight: 700 }}>{t.label}</Typography>
                                        <Typography variant="caption" sx={{ opacity: 0.7 }}>{t.at}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Paper>

                    {/* ITENS */}
                    <Paper sx={{ p: 3, borderRadius: 3, mt: 4 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                            Itens do Pedido
                        </Typography>

                        {(order.items || []).map((item, idx) => (
                            <Box key={`${item.productId || item.id || "item"}-${idx}`}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                    }}
                                >
                                    <Box sx={{ display: "flex", gap: 2 }}>
                                        <Avatar
                                            src={item.image || undefined}
                                            variant="rounded"
                                            sx={{ width: 64, height: 64 }}
                                        />

                                        <Box>
                                            <Typography sx={{ fontWeight: 600 }}>
                                                {item.productName || "-"}
                                            </Typography>
                                            <Typography>Código: {item.productId || "-"}</Typography>
                                            <Typography>Sabor: {item.flavor || "-"}</Typography>
                                        </Box>
                                    </Box>

                                    <Box sx={{ textAlign: "right" }}>
                                        <Typography>{Number(item.quantity || 0)} unidade(s)</Typography>
                                        <Typography sx={{ fontWeight: 600 }}>
                                            {formatBRL((item.price || 0) * (item.quantity || 0))}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Divider sx={{ my: 2 }} />
                            </Box>
                        ))}
                    </Paper>
                </>
            )}
        </Box>
    );
}
